/* ═══════════════════════════════════════════════════════════════
   Stepper — ordered phase progress. Horizontal nodes-and-connectors
   when wide, a vertical list when `compact`. Current step gets the
   accent treatment. The horizontal form measures its own width and
   falls back to the vertical list when the steps would not fit — it
   never scrolls sideways. Labels wrap (never truncate).
   ═══════════════════════════════════════════════════════════════ */

import React, { useLayoutEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { rem } from '../../lib/rem';
import { Icon } from '../ui/Icon';

export interface StepperPhase {
  id: string;
  label: string;
  state: 'done' | 'current' | 'upcoming';
  meta?: React.ReactNode;
}

export interface StepperProps {
  phases: StepperPhase[];
  compact?: boolean;
}

function dotClass(state: StepperPhase['state']): string {
  if (state === 'done') return 'border-primary bg-primary text-inverse';
  if (state === 'current') return 'border-2 border-primary bg-surface text-primary';
  return 'border-border bg-surface text-muted';
}

function StepperNode({ phase }: { phase: StepperPhase }) {
  return (
    <div
      className="flex flex-shrink flex-col items-center gap-1"
      style={{ minWidth: rem(STEP_MIN_REM * 16), maxWidth: rem(96) }}
    >
      <div
        className={cn(
          'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border',
          dotClass(phase.state)
        )}
      >
        {phase.state === 'done' && <Icon icon={Check} size="xs" />}
      </div>
      <span
        className={cn(
          'w-full text-center text-xs',
          phase.state === 'current' ? 'font-semibold text-text' : 'text-muted'
        )}
        style={{ overflowWrap: 'break-word' }}
      >
        {phase.label}
      </span>
      {phase.meta && <span className="block text-center text-xs text-muted">{phase.meta}</span>}
    </div>
  );
}

function StepperRow({ phase }: { phase: StepperPhase }) {
  return (
    <div className="flex items-center gap-2" style={{ minHeight: rem(28) }}>
      <div
        className={cn(
          'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border',
          dotClass(phase.state)
        )}
      >
        {phase.state === 'done' && <Icon icon={Check} size="xs" />}
      </div>
      <span
        style={{ overflowWrap: 'break-word' }}
        className={cn(
          'min-w-0 flex-1 text-sm',
          phase.state === 'current' ? 'font-semibold text-text' : 'text-muted'
        )}
      >
        {phase.label}
      </span>
      {phase.meta && <span className="flex-shrink-0 text-xs text-muted">{phase.meta}</span>}
    </div>
  );
}

/** Minimum horizontal step width (rem) and connector minimum (rem) —
 *  below n·STEP + (n−1)·GAP the horizontal form switches to the list. */
export const STEP_MIN_REM = 4.5;
export const CONNECTOR_MIN_REM = 0.75;

/** Pure orientation decision: does `containerWidthPx` fit `stepCount`
 *  horizontal steps at STEP_MIN_REM with CONNECTOR_MIN_REM between them?
 *  `rootPx` is the current `<html>` font-size (rem base), 16 by default. */
export function stepperFitsHorizontal(
  containerWidthPx: number,
  stepCount: number,
  rootPx = 16
): boolean {
  if (stepCount <= 0) return true;
  const needRem = stepCount * STEP_MIN_REM + Math.max(0, stepCount - 1) * CONNECTOR_MIN_REM;
  return containerWidthPx >= needRem * rootPx;
}

export function Stepper({ phases, compact }: StepperProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [fits, setFits] = useState(true);

  useLayoutEffect(() => {
    if (compact) return;
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      setFits(stepperFitsHorizontal(el.clientWidth, phases.length, rootPx));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [compact, phases.length]);

  if (compact) {
    return (
      <div className="flex flex-col gap-1">
        {phases.map((p) => (
          <StepperRow key={p.id} phase={p} />
        ))}
      </div>
    );
  }
  // The measured wrapper stays mounted in both forms so the observer keeps
  // watching and the horizontal form returns when the card widens.
  return (
    <div ref={ref} className="w-full min-w-0 overflow-hidden">
      {fits ? (
        <div className="flex items-start gap-1">
          {phases.map((p, i) => (
            <React.Fragment key={p.id}>
              {i > 0 && (
                <div
                  className={cn(
                    'h-px flex-1 self-start',
                    p.state === 'upcoming' ? 'bg-border' : 'bg-primary'
                  )}
                  style={{ minWidth: rem(4), marginTop: rem(12) }}
                />
              )}
              <StepperNode phase={p} />
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          {phases.map((p) => (
            <StepperRow key={p.id} phase={p} />
          ))}
        </div>
      )}
    </div>
  );
}
